import React, { useState, useRef, useEffect } from "react";
import { Check, ChevronDown, X } from "lucide-react";

interface MultiSelectProps {
  options: string[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  className?: string;
}

export function MultiSelect({ 
  options, 
  value, 
  onChange, 
  placeholder = "Select...",
  className = ""
}: MultiSelectProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [showAll, setShowAll] = useState(false);

  // Ensure value is always an array
  const safeValue = Array.isArray(value) ? value : [];

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggle = (optionValue: string): void => {
    const newValue = safeValue.includes(optionValue)
      ? safeValue.filter((v) => v !== optionValue)
      : [...safeValue, optionValue];
    onChange(newValue);
  };

  const handleRemove = (optionValue: string, e: React.MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    onChange(safeValue.filter((v) => v !== optionValue));
  };

  const displayText = safeValue.length === 0 
    ? placeholder 
    : `${safeValue.length} selected`;

  return (
    <div className={`w-full relative ${className}`} ref={dropdownRef}>
      {/* Select Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="h-[38px] w-full rounded-[8px] border border-[#D6DADF] bg-white px-3 text-sm outline-none focus:border-[#B5BBC2] text-[#111] flex items-center justify-between hover:border-[#B5BBC2] transition-colors"
      >
        <span className={safeValue.length === 0 ? "text-gray-400" : ""}>
          {displayText}
        </span>
        <ChevronDown 
          className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {/* Dropdown */}
      {isOpen && options.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-[#D6DADF] rounded-[8px] shadow-lg max-h-[240px] overflow-y-auto">
          {options.map((option, idx) => {
            const isSelected = safeValue.includes(option);
            return (
              <div
                key={idx}
                onClick={() => handleToggle(option)}
                className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                  isSelected 
                    ? "bg-blue-600 border-blue-600" 
                    : "border-gray-300"
                }`}>
                  {isSelected && <Check className="w-3 h-3 text-white" />}
                </div>
                <span className="text-sm text-[#111]">{option}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Selected Items Pills */}
      {safeValue.length > 0 && (
  <div className="flex flex-wrap gap-2 mt-2">
    {(showAll ? safeValue : safeValue.slice(0, 2)).map((item, idx) => (
      <div
        key={idx}
        className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 px-2.5 py-1 rounded-md text-sm border border-blue-200"
      >
        <span>{item}</span>
        <button
          type="button"
          onClick={(e) => handleRemove(item, e)}
          className="hover:bg-blue-100 rounded-full p-0.5 transition-colors"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    ))}

    {safeValue.length > 2 && !showAll && (
      <button
        type="button"
        onClick={() => setShowAll(true)}
        className="inline-flex items-center bg-gray-100 text-gray-600 px-2.5 py-1 rounded-md text-sm border border-gray-200 hover:bg-gray-200 transition-colors"
      >
        +{safeValue.length - 2} more
      </button>
    )}

    {safeValue.length > 2 && showAll && (
      <button
        type="button"
        onClick={() => setShowAll(false)}
        className="inline-flex items-center bg-gray-100 text-gray-600 px-2.5 py-1 rounded-md text-sm border border-gray-200 hover:bg-gray-200 transition-colors"
      >
        View less
      </button>
    )}
  </div>
)}
    </div>
  );
}
